// Shared word-matching helpers for the speak-along views (ReadingFlowView and
// LanguagePartnerView). Pure functions — no React, no DOM state.
import { romanize as romanizeKorean } from '@romanize/korean'

// Dashes too ("Мой идеальный день — это…"): a dash is never said.
export const PUNCT_RE = /[.,!?;:"'“”‘’¿¡()«»、。，！？：；·…\-—–―‒„‹›]/g
export const NO_SPACE_LANGS = new Set(['cmn', 'jpn'])

// Split a line into displayable + matchable word tokens. For languages
// written without spaces we fall back to per-character tokens.
//
// Punctuation counts as a word boundary, not something to delete in place:
// models routinely emit "Нормально,спасибо" with no space after the comma, and
// stripping the comma would glue it into one token that spoken audio ("нормально
// спасибо" — two words) can never match.
export const tokenizeLine = (text, lang) => {
  if (!text) return []
  if (NO_SPACE_LANGS.has(lang)) {
    return Array.from(text.replace(/\s+/g, '').replace(PUNCT_RE, ''))
      .map(ch => ({ raw: ch, norm: ch }))
  }
  return text.split(/\s+/).filter(Boolean).flatMap(chunk => {
    const parts = chunk.replace(PUNCT_RE, ' ').split(/\s+/).filter(Boolean)
    if (parts.length === 0) return []                                  // pure punctuation
    if (parts.length === 1) return [{ raw: chunk, norm: parts[0].toLowerCase() }]
    return parts.map(p => ({ raw: p, norm: p.toLowerCase() }))          // was glued together
  })
}

export const tokenizeTranscript = (transcript, lang) => {
  if (NO_SPACE_LANGS.has(lang)) {
    return Array.from(transcript.replace(/\s+/g, '').replace(PUNCT_RE, ''))
  }
  return transcript.replace(PUNCT_RE, ' ').split(/\s+/).map(w => w.toLowerCase()).filter(Boolean)
}

// Small edit-distance so near-misses from the recognizer still count
// (e.g. accents, single-letter endings).
export const editDistance = (a, b) => {
  if (a === b) return 0
  const m = a.length, n = b.length
  if (Math.abs(m - n) > 2) return 99
  const row = Array.from({ length: n + 1 }, (_, j) => j)
  for (let i = 1; i <= m; i++) {
    let prev = row[0]
    row[0] = i
    for (let j = 1; j <= n; j++) {
      const tmp = row[j]
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1))
      prev = tmp
    }
  }
  return row[n]
}

// Recognizers transcribe a spoken number as DIGITS ("30"), while the lesson has
// it written out ("тридцать" / "otuz" / "서른"). Both should count. We map the
// common cardinal number-words per language to their value; `numericValue` then
// returns a number for either a digit token or a number-word, and tokenMatches
// treats two tokens with the same value as equal.
const RAW_NUMBER_WORDS = {
  eng: { zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19, twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90, hundred: 100, thousand: 1000 },
  rus: { ноль: 0, один: 1, одна: 1, одно: 1, два: 2, две: 2, три: 3, четыре: 4, пять: 5, шесть: 6, семь: 7, восемь: 8, девять: 9, десять: 10, одиннадцать: 11, двенадцать: 12, тринадцать: 13, четырнадцать: 14, пятнадцать: 15, шестнадцать: 16, семнадцать: 17, восемнадцать: 18, девятнадцать: 19, двадцать: 20, тридцать: 30, сорок: 40, пятьдесят: 50, шестьдесят: 60, семьдесят: 70, восемьдесят: 80, девяносто: 90, сто: 100, тысяча: 1000 },
  por: { zero: 0, um: 1, uma: 1, dois: 2, duas: 2, três: 3, quatro: 4, cinco: 5, seis: 6, sete: 7, oito: 8, nove: 9, dez: 10, onze: 11, doze: 12, treze: 13, catorze: 14, quatorze: 14, quinze: 15, dezesseis: 16, dezasseis: 16, dezessete: 17, dezassete: 17, dezoito: 18, dezenove: 19, dezanove: 19, vinte: 20, trinta: 30, quarenta: 40, cinquenta: 50, sessenta: 60, setenta: 70, oitenta: 80, noventa: 90, cem: 100, cento: 100, mil: 1000 },
  spa: { cero: 0, uno: 1, un: 1, una: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5, seis: 6, siete: 7, ocho: 8, nueve: 9, diez: 10, once: 11, doce: 12, trece: 13, catorce: 14, quince: 15, dieciséis: 16, diecisiete: 17, dieciocho: 18, diecinueve: 19, veinte: 20, treinta: 30, cuarenta: 40, cincuenta: 50, sesenta: 60, setenta: 70, ochenta: 80, noventa: 90, cien: 100, ciento: 100, mil: 1000 },
  tur: { sıfır: 0, bir: 1, iki: 2, üç: 3, dört: 4, beş: 5, altı: 6, yedi: 7, sekiz: 8, dokuz: 9, on: 10, yirmi: 20, otuz: 30, kırk: 40, elli: 50, altmış: 60, yetmiş: 70, seksen: 80, doksan: 90, yüz: 100, bin: 1000 },
  kor: { 영: 0, 공: 0, 하나: 1, 한: 1, 둘: 2, 두: 2, 셋: 3, 세: 3, 넷: 4, 네: 4, 다섯: 5, 여섯: 6, 일곱: 7, 여덟: 8, 아홉: 9, 열: 10, 스물: 20, 스무: 20, 서른: 30, 마흔: 40, 쉰: 50, 예순: 60, 일흔: 70, 여든: 80, 아흔: 90, 일: 1, 이: 2, 삼: 3, 사: 4, 오: 5, 육: 6, 칠: 7, 팔: 8, 구: 9, 십: 10, 이십: 20, 삼십: 30, 사십: 40, 오십: 50, 육십: 60, 칠십: 70, 팔십: 80, 구십: 90, 백: 100, 천: 1000 },
}
// Normalize a token/key the same way on both sides: strip combining accents
// (Russian stress marks), lowercase, and drop punctuation.
const numNorm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '')
const NUMBER_WORDS = {}
for (const [lg, map] of Object.entries(RAW_NUMBER_WORDS)) {
  NUMBER_WORDS[lg] = {}
  for (const [w, v] of Object.entries(map)) NUMBER_WORDS[lg][numNorm(w)] = v
}
export const numericValue = (token, lang) => {
  const t = numNorm(token)
  if (!t) return null
  if (/^[0-9]+$/.test(t)) return parseInt(t, 10)
  const m = NUMBER_WORDS[lang]
  return m && m[t] != null ? m[t] : null
}

// Deliberately forgiving: a learner mispronouncing an ending, or a recognizer
// dropping a soft sign ("фестивал" for "фестиваль") or hearing "будут" for
// "буду", should still count as having said the word. When a `lang` is given, a
// spoken number ("30") also matches its written-out word ("тридцать").
export const tokenMatches = (spoken, expected, lang) => {
  if (!expected || !spoken) return false
  if (spoken === expected) return true
  if (lang) {
    const sv = numericValue(spoken, lang)
    const ev = numericValue(expected, lang)
    if (sv !== null && ev !== null) return sv === ev
  }
  // Inflection / clipped-ending tolerance.
  const [shorter, longer] = spoken.length < expected.length ? [spoken, expected] : [expected, spoken]
  if (shorter.length >= 4 && longer.startsWith(shorter)) return true
  const d = editDistance(spoken, expected)
  if (expected.length >= 6) return d <= 2
  if (expected.length >= 3) return d <= 1
  return false
}

// Unbounded edit distance — `editDistance` bails early on length gaps, which is
// right for single words but useless for comparing whole phrases.
const fullDistance = (a, b) => {
  const m = a.length, n = b.length
  if (!m) return n
  if (!n) return m
  const row = Array.from({ length: n + 1 }, (_, j) => j)
  for (let i = 1; i <= m; i++) {
    let prev = row[0]
    row[0] = i
    for (let j = 1; j <= n; j++) {
      const tmp = row[j]
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1))
      prev = tmp
    }
  }
  return row[n]
}

// Safety net for per-word matching: recognizers hand back the right words with
// different spacing, punctuation or joins. If the final transcript is within
// ~20% of the whole target, count the line as said rather than making someone
// repeat a phrase they got right.
export const phraseCloseEnough = (transcript, target, lang) => {
  const a = tokenizeTranscript(transcript || '', lang).join(' ')
  const b = tokenizeLine(target || '', lang).map(t => t.norm).filter(Boolean).join(' ')
  if (!a || !b) return false
  if (a === b) return true
  return fullDistance(a, b) <= Math.max(1, Math.floor(b.length * 0.2))
}

// Catch a model answering in the wrong script entirely — qwen in particular
// likes to drop Chinese into a Russian sentence.
const CJK_RE = /[　-〿㐀-䶿一-鿿＀-￯]/
const CJK_LANGS = new Set(['cmn', 'jpn', 'kor'])
export const looksWrongScript = (text, lang) =>
  !CJK_LANGS.has(lang) && CJK_RE.test(String(text || ''))

// The languages the learner speaks natively, from the profile (Hud sets this
// once the profile loads). A native speaker never needs their own language
// romanized — the same rule the flashcard view applies from the profile.
let NATIVE_LANGS = new Set()
export const setNativeLanguages = (codes) => { NATIVE_LANGS = new Set((codes || []).filter(Boolean)) }

// Korean only (deterministic library); everything else is already Latin
// script or has no offline romanizer here.
export const romanizationOf = (text, lang) => {
  if (lang !== 'kor' || !text || NATIVE_LANGS.has(lang)) return null
  try { return romanizeKorean(text) } catch { return null }
}

// TTS holds the audio session; starting recognition while it talks makes the
// session die instantly on Safari/some Chrome versions. Silence it first.
export const cancelTts = () => {
  try { window.speechSynthesis?.cancel() } catch { /* fine */ }
}

// Merge a cumulative recognition transcript into per-word states.
// Hits are sticky so a recognizer restart can't un-hit words.
//
// Matching is order-independent: if you say a word at all, it counts, wherever
// it lands. A fumbled or skipped word can't stall the rest of the line — the
// old forward-only window meant one bad word near the start could block
// everything after it.
export const mergeTranscript = (prevStates, transcript, words, lang, isFinal) => {
  const tokens = tokenizeTranscript(transcript, lang)
  const next = [...prevStates]
  let cursor = 0
  for (const tok of tokens) {
    let best = -1
    let bestDistance = Infinity
    for (let j = 0; j < next.length; j++) {
      if (next[j] === 'hit') continue
      if (!tokenMatches(tok, words[j]?.norm, lang)) continue
      // Prefer the candidate nearest where we're reading, so repeated words
      // fill left-to-right, but allow a match anywhere in the line.
      const d = Math.abs(j - cursor)
      if (d < bestDistance) { bestDistance = d; best = j }
    }
    if (best >= 0) { next[best] = 'hit'; cursor = best + 1 }
  }
  if (isFinal) {
    const lastHit = next.lastIndexOf('hit')
    for (let k = 0; k < lastHit; k++) {
      if (next[k] === 'pending') next[k] = 'miss'
    }
  }
  return next
}

// You've said enough of the line to move on. Demanding every single word be
// perfect traps people on one stubborn word; ~70% (and never more than two
// short) is plenty to prove you said it.
export const lineIsGoodEnough = (states) => {
  if (!states || states.length === 0) return false
  const hits = states.filter(s => s === 'hit').length
  const needed = Math.max(1, Math.min(states.length, Math.ceil(states.length * 0.7)))
  return hits >= needed && (states.length - hits) <= 2
}

// A line as it appears on screen: each word (or, without spaces, each
// character) with where it starts in the text and the range of the matcher's
// tokens it holds [from, to). Punctuation on its own (a dash) holds none:
// never a gap, never to be said.
export const wordUnits = (text, lang) => {
  const units = []
  let t = 0
  if (NO_SPACE_LANGS.has(lang)) {
    let at = 0
    for (const ch of Array.from(text)) {
      if (!/\s/.test(ch)) {
        const real = ch.replace(PUNCT_RE, '') !== ''
        units.push({ raw: ch, start: at, from: t, to: real ? t + 1 : t })
        if (real) t += 1
      }
      at += ch.length
    }
    return { units, tokens: tokenizeLine(text, lang), spaced: false }
  }
  const tokens = []
  for (const m of text.matchAll(/\S+/g)) {
    const mine = tokenizeLine(m[0], lang)
    units.push({ raw: m[0], start: m.index, from: t, to: t + mine.length })
    tokens.push(...mine)
    t += mine.length
  }
  return { units, tokens, spaced: true }
}
